import React, { useMemo } from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Popover } from 'antd';

import { AGGREGATION_FIELDS, TIER_CONFIG } from '@/shared/api/openmetadata';
import type { OpenMetadataAggregations } from '@/shared/api/openmetadata';
import { formatTierName } from '@/shared/lib/format';
import { highlightText } from '@/shared/lib/text';
import { FilterDropdown } from '@/shared/ui/FilterDropdown';
import type { FilterDropdownOption } from '@/shared/ui/FilterDropdown';

import { useFilterStore } from '@/entities/discovery';
import { useSearchStore } from '@/entities/search/domain/store';

import styles from './TierDropdown.module.css';

const detectTierKeyFormat = (aggregations: OpenMetadataAggregations | null): string => {
  const tierBuckets = aggregations?.[AGGREGATION_FIELDS.TIER]?.buckets || [];
  if (tierBuckets.length === 0) return TIER_CONFIG.DEFAULT_PREFIX;

  return tierBuckets[0].key.split('.')[0];
};

const generateTierOptionsFromAggregations = (
  initialAggregations: OpenMetadataAggregations | null,
  selectedTiers: string[]
): FilterDropdownOption[] => {
  const tierBuckets = initialAggregations?.[AGGREGATION_FIELDS.TIER]?.buckets || [];
  const tierPrefix = detectTierKeyFormat(initialAggregations);

  const tierCountMap = new Map<string, number>();
  tierBuckets.forEach((bucket) => {
    const normalizedKey = bucket.key.toLowerCase();
    tierCountMap.set(normalizedKey, bucket.doc_count);
  });

  const selectedSet = new Set(selectedTiers);

  const options: FilterDropdownOption[] = TIER_CONFIG.NUMBERS.map((tierNum) => {
    const tierKey = `${tierPrefix}.${tierPrefix}${tierNum}`;
    const normalizedKey = tierKey.toLowerCase();
    const count = tierCountMap.get(normalizedKey) || 0;
    const label = formatTierName(`Tier${tierNum}`);

    return {
      key: tierKey,
      label,
      value: tierKey,
      disabled: false,
      data: {
        sortOrder: tierNum,
        count,
      },
    };
  });

  options.sort((a, b) => {
    const aSelected = selectedSet.has(a.key);
    const bSelected = selectedSet.has(b.key);

    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;

    const aOrder = typeof a.data?.sortOrder === 'number' ? a.data.sortOrder : 999;
    const bOrder = typeof b.data?.sortOrder === 'number' ? b.data.sortOrder : 999;
    return aOrder - bOrder;
  });

  return options;
};

type TierDropdownProps = {
  width?: number | string;
};

export const TierDropdown: React.FC<TierDropdownProps> = ({ width }) => {
  const { filters, updateTierFilter } = useFilterStore();
  const { initialAggregations } = useSearchStore();

  const tierOptions = useMemo(() => {
    const selectedTiers = filters.tier || [];
    return generateTierOptionsFromAggregations(initialAggregations, selectedTiers);
  }, [initialAggregations, filters.tier]);

  return (
    <FilterDropdown
      buttonText="중요 등급"
      searchPlaceholder="중요 등급 검색..."
      selectedValues={filters.tier || []}
      options={tierOptions}
      onSelectionChange={updateTierFilter}
      width={width}
      renderItem={(option, _isSelected, _onToggle, searchQuery) => {
        const description = typeof option.data?.description === 'string' ? option.data.description : undefined;

        return (
          <div className={styles.tierHeader}>
            <div className={styles.tierContent}>
              <span className={styles.tierLabel}>
                {highlightText(option.label, searchQuery, { className: 'highlight' })}
              </span>
            </div>
            {description && (
              <Popover
                content={<div className={styles.popoverContent}>{description}</div>}
                placement="topLeft"
                trigger="hover"
                arrow={false}>
                <QuestionCircleOutlined className={styles.questionIcon} />
              </Popover>
            )}
          </div>
        );
      }}
    />
  );
};
